import React, { useEffect, useState, useCallback } from 'react';
import { View, StyleSheet, ActivityIndicator, Text, FlatList, RefreshControl } from 'react-native';
import Web3 from 'web3';
import { globalConfigurableData, GlobalScreens } from '../../utils/globalConstant';
import Colors from '../../assets/Color/Colors';
import { globalFontStyle, globalStyle } from '../../assets';
import { isMobile } from '../../utils/commonMethods';
import Header from '../../commonComponents/Header';
import FormTextLabel from '../../commonComponents/FormTextLabel';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { defaulteventimage } from '../../utils/images';
import { Image } from 'expo-image';
import { useSelector } from 'react-redux';
import ProtectedRoute from '../../commonComponents/ProtectedRoute';

const ABI = [
  {
    "type": "function",
    "name": "getAllTransactions",
    "inputs": [
      {
        "name": "_limit",
        "type": "uint256",
        "internalType": "uint256"
      },
      {
        "name": "_offset",
        "type": "uint256",
        "internalType": "uint256"
      }
    ],
    "outputs": [
      {
        "name": "",
        "type": "tuple[]",
        "internalType": "struct Transactions.Transaction[]",
        "components": [
          {
            "name": "id",
            "type": "int256",
            "internalType": "int256"
          },
          {
            "name": "userId",
            "type": "bytes16",
            "internalType": "bytes16"
          },
          {
            "name": "amount",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "transactionType",
            "type": "uint8",
            "internalType": "enum Transactions.TransactionType"
          },
          {
            "name": "txnId",
            "type": "string",
            "internalType": "string"
          },
          {
            "name": "message",
            "type": "string",
            "internalType": "string"
          },
          {
            "name": "timestamp",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "blockTimestamp",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "walletType",
            "type": "string",
            "internalType": "string"
          },
          {
            "name": "surcharge",
            "type": "uint256",
            "internalType": "uint256"
          },
          {
            "name": "refId",
            "type": "string",
            "internalType": "string"
          },
          {
            "name": "probeId",
            "type": "uint256",
            "internalType": "uint256"
          }
        ]
      }
    ],
    "stateMutability": "view"
  }
];

const stripEmojis = (text) => {
  return text.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '');
};

const Activity = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const transactionsPerPage = 20;
  const insets = useSafeAreaInsets();

  // Get probes data from the app state
  const probeData = useSelector(state => state.homeReducer?.probeData);

  // Function to get event image based on probeId
  const getEventImage = (probeId) => {
    if (!probeId) return defaulteventimage();
    
    const probe = probeData?.probes?.find(p => p.id === probeId);
    return probe?.imageurl ? { uri: probe.imageurl } : defaulteventimage();
  };

  const fetchTransactions = async (isLoadMore = false, isRefresh = false) => {
    try {
      const rpcUrl = process.env.NODE_ENV === 'production' 
        ? 'https://subnets.avax.network/trx/mainnet/rpc'
        : 'https://subnets.avax.network/trxt/testnet/rpc';
      
      const web3Instance = new Web3(new Web3.providers.HttpProvider(rpcUrl));
      
      const contractAddress = process.env.NODE_ENV === 'production'
        ? '0xAe27d75ba258A5d24bC3798362FD95388d1AF41B'
        : '0x02900e54413E7246E18FA4c097aCd9dc5Bcd662a';

      const contract = new web3Instance.eth.Contract(ABI, contractAddress);
      
      const currentOffset = isRefresh ? 0 : offset;
      const txns = await contract.methods.getAllTransactions(transactionsPerPage, currentOffset).call();
      
      // Filter for executed trades only and exclude settled and merged transactions
      const executedTrades = txns.filter(txn => {
        const message = txn.message.toLowerCase();
        // Exclude settled and merged transactions
        if (message.includes('settled') || message.includes('merged')) return false;
        
        // Include only bought, sold, or cancelled transactions
        return message.includes('bought') || 
               message.includes('sold') || 
               message.includes('cancelled');
      });

      // Sort transactions by timestamp in descending order
      const sortedTransactions = executedTrades.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));

      if (isLoadMore) {
        setTransactions(prev => {
          // Filter out duplicates based on transaction ID
          const existingIds = new Set(prev.map(t => t.id));
          const newTxns = sortedTransactions.filter(t => !existingIds.has(t.id));
          return [...prev, ...newTxns];
        });
      } else {
        setTransactions(sortedTransactions);
      }

      // Update hasMore based on whether we received fewer transactions than requested
      setHasMore(sortedTransactions.length === transactionsPerPage);
      
      if (!isLoadMore) {
        setOffset(transactionsPerPage);
      } else {
        setOffset(prev => prev + transactionsPerPage);
      }
      
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to fetch transactions. Please try again later.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchTransactions();
  }, []);

  // Auto-update every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading && !loadingMore && !refreshing) {
        fetchTransactions(false, true);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [loading, loadingMore, refreshing]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchTransactions(false, true);
  }, []);

  const onEndReached = () => {
    if (!loadingMore && !loading && !refreshing && hasMore) {
      setLoadingMore(true);
      fetchTransactions(true);
    }
  };

  const renderFooter = () => {
    if (!loadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={Colors.tradexBlue} />
      </View>
    );
  };

  const getRelativeTime = (timestamp) => {
    const now = Math.floor(Date.now() / 1000);
    const seconds = now - Number(timestamp);

    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    } else if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      return `${hours}h`;
    } else {
      const days = Math.floor(seconds / 86400);
      return `${days}d`;
    }
  };

  const renderTransaction = ({ item }) => {
    const relativeTime = getRelativeTime(item.timestamp);

    // Parse message to separate event name and trade activity
    const parseMessage = (message) => {
      // Strip emojis from the message
      const cleanMessage = stripEmojis(message);
      const parts = cleanMessage.split('Market:');
      if (parts.length < 2) return { eventName: cleanMessage, tradeActivity: '' };

      // Split by question mark if exists, otherwise take the whole string before action keywords
      const eventPart = parts[1].trim();
      let eventName, tradeActivity;

      // Look for action keywords (excluding merged)
      const keywords = [' bought ', ' sold ', ' cancelled '];
      let splitIndex = -1;
      let foundKeyword = '';

      // Find the first occurring keyword
      keywords.forEach(keyword => {
        const index = eventPart.toLowerCase().indexOf(keyword);
        if (index !== -1 && (splitIndex === -1 || index < splitIndex)) {
          splitIndex = index;
          foundKeyword = keyword.trim();
        }
      });

      if (splitIndex !== -1) {
        eventName = eventPart.substring(0, splitIndex).trim();
        // If there's a question mark in the event name, keep it
        if (eventName.includes('?')) {
          eventName = eventName;
        }
        tradeActivity = eventPart.substring(splitIndex + 1).split(',')[0].trim();
      } else {
        // If no action keyword found, return the whole message as event name
        eventName = eventPart;
        tradeActivity = '';
      }

      return { eventName, tradeActivity };
    };

    const { eventName, tradeActivity } = parseMessage(item.message);

    // Update formatTradeActivity to handle all transaction types
    const formatTradeActivity = (text) => {
      if (!text) return null;
      
      const parts = text.split(' x ');
      if (parts.length !== 2 && !text.includes('merged') && !text.includes('cancelled')) return text;

      // Format userId with n prefix and first 5 characters
      const userId = item.userId.toString();
      const truncatedUserId = `n${userId.substring(0, 5)}`;

      // Handle merged and cancelled cases
      if (text.includes('merged') || text.includes('cancelled')) {
        return (
          <Text style={[globalFontStyle.appFontSubText, { fontWeight: '400' }]}>
            <Text style>{truncatedUserId}</Text>
            {' ' + text.toLowerCase()}
          </Text>
        );
      }

      // Handle bought/sold cases
      const [firstPart, secondPart] = parts;
      const [yesNo, ...restParts] = secondPart.split(' ');
      const rest = restParts.join(' ');

      return (
        <Text style={[globalFontStyle.appFontSubText, { fontWeight: '400' }]}>
          <Text style>
            {truncatedUserId}
          </Text>
          {' ' + firstPart.toLowerCase() + ' x '}
          <Text style={{ 
            color: yesNo.toLowerCase() === 'y' ? Colors.green : Colors.red,
            fontWeight: '500'
          }}>
            {yesNo.toLowerCase() === 'y' ? 'Yes' : 'No'}
          </Text>
          {' ' + rest}
        </Text>
      );
    };

    // Calculate image size based on screen width
    const getImageSize = () => {
      if (isMobile()) {
        return 45;
      }
      return 55;
    };

    const imageSize = getImageSize();

    return (
      <View style={styles.transactionItem}>
        <View style={styles.transactionRow}>
          <Image
            style={[styles.transactionImage, { width: imageSize, height: imageSize }]}
            source={getEventImage(item.probeId)}
            contentFit="cover"
            transition={1000}
          />
          <View style={styles.transactionMainContent}>
            <View style={styles.transactionTitleRow}>
              <Text 
                style={[
                  globalFontStyle.appFontSubText,
                  { 
                    color: globalConfigurableData.secondaryTextColor1,
                    flex: 1,
                    marginRight: 8,
                    lineHeight: 20
                  }
                ]}
                numberOfLines={2}
              >
                {eventName}
              </Text>
              <Text style={[
                globalFontStyle.appFontBody,
                { 
                  marginLeft: 8,
                  color: globalConfigurableData.secondaryTextColor1,
                }
              ]}>
                {relativeTime}
              </Text>
            </View>
          </View>
        </View>
        <View 
          style={[
            styles.activityContainer,
            { 
              paddingHorizontal: 16,
              marginTop: 8,
              marginBottom: 8
            }
          ]}
        >
          {formatTradeActivity(tradeActivity)}
        </View>
        <View style={styles.divider} />
      </View>
    );
  };

  return (
    <View style={[styles.container, { 
      paddingTop: insets.top,
      paddingLeft: insets.left,
      paddingRight: insets.right,
    }]}>
      {!isMobile() ? (
        <Header selected={'Activity'} />
      ) : (
        <View style={globalStyle.headerContainer}>
          <FormTextLabel
            labelStyles={[
              {
                ...globalFontStyle.appBlueHead,
                color: globalConfigurableData.primaryTextColor1,
              },
            ]}
            label={'Activity'}
          />
        </View>
      )}
      
      {loading && !loadingMore ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Colors.tradexBlue} />
        </View>
      ) : error && !transactions.length ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : (
        <View style={styles.contentContainer}>
          {transactions.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No transactions found</Text>
            </View>
          ) : (
            <FlatList
              data={transactions}
              renderItem={renderTransaction}
              keyExtractor={item => item.id.toString()}
              contentContainerStyle={styles.listContainer}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={onRefresh}
                  colors={[Colors.tradexBlue]}
                  tintColor={Colors.tradexBlue}
                />
              }
              onEndReached={onEndReached}
              onEndReachedThreshold={0.5}
              ListFooterComponent={renderFooter}
            />
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: globalConfigurableData.globalThemeColor,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: globalConfigurableData.globalThemeColor,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: globalConfigurableData.globalThemeColor,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: globalConfigurableData.globalThemeColor,
    padding: 20,
  },
  errorText: {
    color: Colors.red,
    textAlign: 'center',
    fontSize: 16,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: globalConfigurableData.globalThemeColor,
  },
  emptyText: {
    color: Colors.grey,
    fontSize: 16,
  },
  listContainer: {
  },
  transactionItem: {
    marginTop: isMobile() ? 15 : 20,
    backgroundColor: globalConfigurableData.cardBgColor,
  },
  transactionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  transactionMainContent: {
    flex: 1,
  },
  transactionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  transactionDetailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  divider: {
    height: 1,
    backgroundColor: Colors.lightGrey,
    opacity: 0.1,
  },
  footerLoader: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  transactionImage: {
    borderRadius: 4,
    marginRight: 12,
    overflow: 'hidden',
  },
  activityContainer: {
    // Add any additional styles for the activity container here
  },
});

const WrappedActivity = (props) => (
  <ProtectedRoute>
    <Activity {...props} />
  </ProtectedRoute>
);

export default WrappedActivity; 